import { reactive, computed, watch } from 'vue';

// Model for Driver Onboarding - Step 1: Personal Information
// Adds DOM class toggling similar to DispatchUser.js
export default function DriverOnboardingPersonalInfo(data = {}) {
  const state = reactive({
    firstName: data.firstName || '',
    lastName: data.lastName || '',
    email: data.email || '',
    countryCode: data.countryCode || '+1',
    phoneNumber: data.phoneNumber || '',
    address: data.address || '',
    city: data.city || '',
    state: data.state || '',
    country: data.country || '',
    postalCode: data.postalCode || '',
    dateOfBirth: data.dateOfBirth || '', // ISO YYYY-MM-DD
  });

  const isIsoDate = (val) => {
    if (!val) return false;
    const m = /^\d{4}-\d{2}-\d{2}$/.exec(val);
    if (!m) return false;
    const dt = new Date(val);
    const [y, mo, d] = val.split('-').map(Number);
    return dt instanceof Date && !isNaN(dt) && dt.getUTCFullYear() === y && (dt.getUTCMonth() + 1) === mo && dt.getUTCDate() === d;
  };

  const validations = {
    isFirstNameValid: computed(() => !!String(state.firstName ?? '').trim()),
    isLastNameValid: computed(() => !!String(state.lastName ?? '').trim()),
    isEmailValid: computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(state.email ?? ''))),
    isPhoneNumberValid: computed(() => /^[0-9]{7,15}$/.test(String(state.phoneNumber ?? ''))),
    isAddressValid: computed(() => !!String(state.address ?? '').trim()),
    isCityValid: computed(() => !!String(state.city ?? '').trim()),
    isStateValid: computed(() => !!String(state.state ?? '').trim()),
    isCountryValid: computed(() => !!String(state.country ?? '').trim()),
    isPostalCodeValid: computed(() => !!String(state.postalCode ?? '').trim()),
    isDobValid: computed(() => {
      if (!isIsoDate(String(state.dateOfBirth ?? ''))) return false;
      
      // Validate age (must be at least 18 years old)
      const dob = new Date(state.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      const dayDiff = today.getDate() - dob.getDate();
      
      // Adjust age if birthday hasn't occurred this year yet
      const actualAge = (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) ? age - 1 : age;
      
      // Must be at least 18 and not more than 100 years old
      return actualAge >= 18 && actualAge <= 100;
    }),
  };

  // DOM helpers (using global jQuery just like DispatchUser.js)
  const toggleInvalid = (inputSelector, errorSelector, ok) => {
    const $input = $(inputSelector);
    const $error = $(errorSelector);
    if (!$input || !$input.length) return ok; // if DOM not present, no-op

    if (ok) {
      $input.removeClass('is-invalid');
      if ($error && $error.length) $error.addClass('d-none');
    } else {
      $input.addClass('is-invalid');
      if ($error && $error.length) $error.removeClass('d-none');
    }
    return ok;
  };

  const validateFirstName = () => toggleInvalid('#driver-first-name-input', '#driver-first-name-error', validations.isFirstNameValid.value);
  const validateLastName = () => toggleInvalid('#driver-last-name-input', '#driver-last-name-error', validations.isLastNameValid.value);
  const validateDob = () => toggleInvalid('#driver-dob-input', '#driver-dob-error', validations.isDobValid.value);
  const validatePhone = () => toggleInvalid('#driver-phone-number-input', '#driver-phone-number-error', validations.isPhoneNumberValid.value);
  const validateAddress = () => toggleInvalid('#driver-address-input', '#driver-address-error', validations.isAddressValid.value);
  const validateCity = () => toggleInvalid('#driver-city-input', '#driver-city-error', validations.isCityValid.value);
  const validateState = () => toggleInvalid('#driver-state-input', '#driver-state-error', validations.isStateValid.value);
  const validateCountry = () => toggleInvalid('#driver-country-input', '#driver-country-error', validations.isCountryValid.value);
  const validatePostalCode = () => toggleInvalid('#driver-postal-code-input', '#driver-postal-code-error', validations.isPostalCodeValid.value);

  // Watchers for real-time validation
  watch(() => state.firstName, validateFirstName);
  watch(() => state.lastName, validateLastName);
  watch(() => state.dateOfBirth, validateDob);
  watch(() => state.phoneNumber, validatePhone);
  watch(() => state.address, validateAddress);
  watch(() => state.city, validateCity);
  watch(() => state.state, validateState);
  watch(() => state.country, validateCountry);
  watch(() => state.postalCode, validatePostalCode);

  const isValid = () => (
    validateFirstName() |
    validateLastName() |
    /* Email is read-only here; still ensure format for completeness */
    validations.isEmailValid.value |
    validatePhone() |
    validateAddress() |
    validateCity() |
    validateState() |
    validateCountry() |
    validatePostalCode() |
    validateDob()
  );

  const toJSON = () => ({ ...state });

  return { state, validations, isValid, toJSON };
}
